<template>
  <div v-if="dialog" class="product-quick-view">
    <div class="product-quick-view__backdrop" @click="close"></div>
    <UiCard class="product-quick-view__card">
      <div class="product-quick-view__header">
        <h3>Быстрый просмотр товара</h3>
        <button type="button" class="product-quick-view__link" @click="close">Закрыть</button>
      </div>

      <div class="product-quick-view__content">
        <div class="product-quick-view__image">
          <img
            :src="product.images?.[0] || product.imageUrl || '/placeholder-product.svg'"
            :alt="product.name"
            @error="handleImageError"
          />
        </div>

        <div class="product-quick-view__info">
          <UiBadge variant="neutral">{{ product.categoryName }}</UiBadge>
          <h4>{{ product.name }}</h4>
          <div class="product-quick-view__muted">Артикул: {{ product.sku }}</div>

          <div class="product-quick-view__price">
            <span class="product-quick-view__price-main">{{ formatPriceWithCents(product.price) }}</span>
            <UiBadge
              v-if="product.originalPrice && product.originalPrice > product.price"
              variant="accent"
            >
              -{{ Math.round((1 - product.price / product.originalPrice) * 100) }}%
            </UiBadge>
          </div>
          <div
            v-if="product.originalPrice && product.originalPrice > product.price"
            class="product-quick-view__muted"
          >
            {{ formatPriceWithCents(product.originalPrice) }}
          </div>
          <div
            v-if="product.minimumOrder && product.minimumOrder > 1"
            class="product-quick-view__muted"
          >
            Минимальный заказ: {{ product.minimumOrder }} {{ product.unit?.symbol || 'шт.' }}
          </div>

          <div class="product-quick-view__description">
            <h5>Описание</h5>
            <p>{{ product.description || 'Описание товара отсутствует' }}</p>
          </div>

          <div
            v-if="product.specifications && Object.keys(product.specifications).length > 0"
            class="product-quick-view__specs"
          >
            <h5>Основные характеристики</h5>
            <ul>
              <li v-for="(value, key) in limitedSpecifications" :key="key">
                <strong>{{ key }}:</strong> {{ value }}
              </li>
            </ul>
            <button
              v-if="Object.keys(product.specifications).length > 3"
              type="button"
              class="product-quick-view__link"
              @click="goToProduct"
            >
              Все характеристики ({{ Object.keys(product.specifications).length }})
            </button>
          </div>

          <div class="product-quick-view__actions">
            <UiButton variant="secondary" :disabled="addingToCart" @click="addToCart">
              Добавить в корзину
            </UiButton>
            <UiButton variant="secondary" @click="toggleFavorite">
              {{ isFavorite ? 'В избранном' : 'В избранное' }}
            </UiButton>
          </div>
        </div>
      </div>

      <div class="product-quick-view__footer">
        <UiButton variant="secondary" @click="goToProduct">Перейти к товару</UiButton>
        <UiButton variant="secondary" @click="close">Закрыть</UiButton>
      </div>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useCartActions } from '@/modules/order/public'
import { useFavoritesActions } from '@/modules/profile/public'
import { useSnackbar } from '@/modules/common/public'
import type { Product } from '@/types/catalog'
import { formatPriceWithCents } from '@/utils/price'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'

interface Props {
  modelValue: boolean
  product: Product
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const router = useRouter()
const cartActions = useCartActions()
const favoritesActions = useFavoritesActions()
const {
  showSuccess: showSnackbarSuccess,
  showError: showSnackbarError,
  showInfo: showSnackbarInfo,
} = useSnackbar()

const addingToCart = ref(false)
const fallbackImage = '/placeholder-product.svg'

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const isFavorite = computed(() => {
  return favoritesActions.isFavorite(props.product.id)
})

const limitedSpecifications = computed(() => {
  if (!props.product.specifications) return {}

  const entries = Object.entries(props.product.specifications)
  const limited = entries.slice(0, 3)
  return Object.fromEntries(limited)
})

function handleImageError(event: Event) {
  const target = event.target as HTMLImageElement | null
  if (!target) return
  if (!target.src.endsWith(fallbackImage)) {
    target.src = fallbackImage
  }
}

function close() {
  dialog.value = false
}

function goToProduct() {
  close()
  router.push(`/product/${props.product.id}`)
}

async function addToCart() {
  if (addingToCart.value) return

  addingToCart.value = true
  try {
    await cartActions.addItem({
      productId: props.product.id,
      name: props.product.name,
      price: props.product.price,
      sku: props.product.sku,
      image: props.product.images?.[0] || props.product.imageUrl || '/placeholder-product.svg',
      quantity: props.product.minimumOrder ?? 1,
    })
    showSnackbarSuccess(`Товар "${props.product.name}" добавлен в корзину`)
  } catch (error) {
    showSnackbarError(error instanceof Error ? error.message : 'Ошибка при добавлении в корзину')
  } finally {
    addingToCart.value = false
  }
}

function toggleFavorite() {
  favoritesActions
    .toggleFavorite(props.product)
    .then((result) => {
      if (result.added) {
        showSnackbarSuccess('Товар добавлен в избранное', 4000, {
          label: 'Перейти в избранное',
          to: '/favorites',
        })
      } else if (result.removed) {
        showSnackbarInfo('Товар удален из избранного')
      } else if (result.isFavorite) {
        showSnackbarSuccess('Товар добавлен в избранное', 4000, {
          label: 'Перейти в избранное',
          to: '/favorites',
        })
      } else {
        showSnackbarInfo('Товар удален из избранного')
      }
    })
    .catch(() => {
      showSnackbarError('Ошибка при работе с избранным')
    })
}
</script>

<style scoped>
.product-quick-view {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-quick-view__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
}

.product-quick-view__card {
  position: relative;
  width: min(900px, 92vw);
  max-height: 90vh;
  overflow: auto;
}

.product-quick-view__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.product-quick-view__content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.product-quick-view__image img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
}

.product-quick-view__info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.product-quick-view__muted {
  font-size: 12px;
  color: #586578;
}

.product-quick-view__price {
  display: flex;
  gap: 8px;
  align-items: center;
}

.product-quick-view__price-main {
  font-size: 20px;
  font-weight: 700;
}

.product-quick-view__description p {
  color: #475569;
}

.product-quick-view__specs ul {
  padding-left: 16px;
  margin: 0;
  color: #475569;
}

.product-quick-view__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.product-quick-view__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.product-quick-view__link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

@media (max-width: 960px) {
  .product-quick-view__content {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
